import React, { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Button, Typography } from '@mui/material';
import { useAuthContext } from 'AuthContext';
import MeetingService from 'services/MeetingService';
import Card from 'components/shared/Card';
import VoteResultView from '../../VoteResultView';
import {
  ActivityLog,
  ApprovalLevel,
  Comment,
  Document,
  IPCheckSession,
  Motion,
  Reject,
  TechnicalIssue,
  TechnologyConflict,
  VotingSession,
} from 'store/types/Meeting';
import { ActivityType, QuestionResponse, TechnologyType, WithdrawalType } from 'store/enums/BallotReviewTypes';
import { getFormattedDate } from 'util/format';
import { defaultSnackbarErrorProps } from 'util/layout';
import { activityTypeLabel, ipCheckTypeLabel, votingRuleTypeLabel, withdrawalTypeLabel } from 'util/meeting';

import styles from './ActivityLogViewCard.module.scss';
import commonStyles from 'styles/common.module.scss';

interface ActivityLogViewCardProps {
  activityLog: ActivityLog;
}

const motionView = (motion: Motion, votingSession?: VotingSession) => (
  <Box className={styles.section}>
    <Typography>
      <strong>Title:</strong> {motion.title}
    </Typography>
    {motion.fromText && (
      <>
        <Typography>
          <strong>From:</strong>
        </Typography>
        <Box sx={{ typography: 'body1' }} dangerouslySetInnerHTML={{ __html: motion.fromText }} />
      </>
    )}
    {motion.toText && (
      <>
        <Typography>
          <strong>To:</strong>
        </Typography>
        <Box sx={{ typography: 'body1' }} dangerouslySetInnerHTML={{ __html: motion.toText }} />
      </>
    )}
    {motion.justification && (
      <>
        <Typography>
          <strong>Justification:</strong>
        </Typography>
        <Box sx={{ typography: 'body1' }} dangerouslySetInnerHTML={{ __html: motion.justification }} />
      </>
    )}
    <Box display={'flex'}>
      <Box
        sx={{ typography: 'body1' }}
        dangerouslySetInnerHTML={{ __html: '<strong>Motion: </strong>' + motion.statement }}
      />
    </Box>
    {motion.reason && (
      <>
        <Typography>
          <strong>Reason:</strong>
        </Typography>
        <Box sx={{ typography: 'body1' }} dangerouslySetInnerHTML={{ __html: motion.reason }} />
      </>
    )}
    {motion.by && (
      <Typography>
        <strong>By:</strong> {motion.by}
      </Typography>
    )}
    {motion.second && (
      <Typography>
        <strong>Second:</strong> {motion.second}
      </Typography>
    )}
    {motion.discussion && (
      <>
        <Typography>
          <strong>Discussion:</strong>
        </Typography>
        <Box sx={{ typography: 'body1' }} dangerouslySetInnerHTML={{ __html: motion.discussion }} />
      </>
    )}
    {votingSession?.result && (
      <>
        <Typography>
          <strong>Voting Rule:</strong> {votingRuleTypeLabel[votingSession.result.rule]}
        </Typography>
        <Typography>
          <strong>Result:</strong>
          {/* {' '}
          {`${votingSession.result.tally?.yes}-Y ${votingSession.result.tally?.no}-N Voting Result: ${
            votingSession.result.passed ? 'Pass' : 'Fail'
          } - ${Number(votingSession.result.percentageYes * 100).toFixed(0) || 0}%`} */}
        </Typography>
        <Box sx={{ pl: 3 }}>
          <VoteResultView votingResult={votingSession.result} />
        </Box>
      </>
    )}
    {motion.recommendsTechnicalChange && (
      <Typography sx={{ mt: 3 }}>
        <strong>Is a technical change recommended?:</strong> {motion.recommendsTechnicalChange}
      </Typography>
    )}
  </Box>
);

const documentView = (document: Document) => {
  const { number, title, lineItemNumber, lineItemTitle } = document;

  return (
    <Box className={styles.section}>
      <Typography>
        <strong>Document Number:</strong> {number}
      </Typography>
      <Typography>
        <strong>Document Title:</strong> {title}
      </Typography>
      {(lineItemNumber || lineItemTitle) && (
        <>
          <Typography>
            <strong>Line Item Number:</strong> {lineItemNumber}
          </Typography>
          <Typography>
            <strong>Line Item Title:</strong> {lineItemTitle}
          </Typography>
        </>
      )}
    </Box>
  );
};

const rejectView = (reject: Reject) => (
  <Box className={styles.section}>
    <Typography>
      <strong>Reject Affiliate:</strong> {reject.votingInterest}
    </Typography>
    <Typography>
      <strong>Rejected By:</strong> {reject.rejectedBy}
    </Typography>
    <Typography>
      <strong>Negative Reference:</strong> {reject.reference}
    </Typography>
    <Typography>
      <strong>Negative Text:</strong>
    </Typography>
    <Box sx={{ typography: 'body1' }} dangerouslySetInnerHTML={{ __html: reject.text }} />
    {reject.taskForceInput && (
      <Typography>
        <strong>TF Input:</strong> {reject.taskForceInput}
      </Typography>
    )}
    {reject.withdrawal && (
      <Typography>
        <strong>Withdrawal: </strong>
        {`${withdrawalTypeLabel[reject.withdrawal]} ${
          reject.withdrawal === WithdrawalType.WithdrawalMade
            ? `on ${getFormattedDate(reject.withdrawalDate ?? '', 'L')}`
            : ''
        }`}
      </Typography>
    )}
  </Box>
);

const technicalIssueView = (technicalIssue: TechnicalIssue) => (
  <Box className={styles.section}>
    <Typography>
      <strong>Origin:</strong> {technicalIssue.origin}
    </Typography>
    <Typography>
      <strong>Referenced Section:</strong> {technicalIssue.reference}
    </Typography>
    <Typography>
      <strong>Reason:</strong> {technicalIssue.reason}
    </Typography>
  </Box>
);

const commentView = (comment: Comment) => (
  <Box className={styles.section}>
    <Typography>
      <strong>Commenter:</strong> {comment.commenter}
    </Typography>
    <Typography>
      <strong>Comment Number:</strong> {comment.number}
    </Typography>
    {comment.text && (
      <>
        <Typography>
          <strong>Comment Text:</strong>
        </Typography>
        <Box sx={{ typography: 'body1' }} dangerouslySetInnerHTML={{ __html: comment.text }} />
      </>
    )}
    {comment.action && (
      <Typography>
        <strong>Action:</strong> {comment.action}
      </Typography>
    )}
    {comment.actionCommenterNumber && (
      <Typography>
        <strong>Commenter:</strong> {comment.actionCommenterNumber}
      </Typography>
    )}
    {comment.actionCommentNumber && (
      <Typography>
        <strong>Comment Number:</strong> {comment.actionCommentNumber}
      </Typography>
    )}
    {comment.case && (
      <Typography>
        <strong>Case:</strong> {comment.case}
      </Typography>
    )}
  </Box>
);

const approvalLevelView = (approvalLevel: ApprovalLevel) => (
  <Box className={styles.section}>
    <Typography>
      <strong>Approval Level:</strong> {approvalLevel.statement}
    </Typography>
  </Box>
);

const ipCheckSessionView = (ipCheckSession: IPCheckSession) => {
  const { type, patentConflict, patentSummary, copyrightConflict, copyrightSummary, completionConditionCheck } =
    ipCheckSession;
  return (
    <>
      <Box className={styles.section}>
        <Typography>
          <strong>Intellectual Property (IP) Check</strong>
        </Typography>
        <Typography>
          <strong>Result:</strong> {ipCheckTypeLabel[type]}
        </Typography>
        {patentConflict && (
          <Typography>
            <strong>Is any of the known IPs a patented technology?: </strong>
            {patentConflict === QuestionResponse.Yes ? 'Yes, at least one of them is a patented technology' : 'No'}
          </Typography>
        )}
      </Box>
      {patentSummary && (
        <Box className={styles.section}>
          <Typography>
            <strong>Total numbers of Patented Technologies to be dealt with</strong>
          </Typography>
          <Typography>
            <strong>Known Patented Technology that might be relevant to the Standard/Safety Guideline: </strong>
            {patentSummary.totalConflicts}
          </Typography>
          <Typography>
            <strong>
              Number of patented technologies first became known to the TC Chapter on or after the day of the issuance
              of this Letter Ballot{' '}
              <span className={commonStyles.redText}>
                (Postpone assessment of such patented technologies to be performed at the next scheduled TC Chapter
                meeting.)
              </span>
              :{' '}
            </strong>
            {patentSummary.conflictsAfterIssuance}
          </Typography>
          <Typography>
            <strong>
              Number of patented technologies first became known to the TC Chapter before the day of the issuance of
              this Letter Ballot:{' '}
            </strong>
            {patentSummary.conflictsBeforeIssuance}
          </Typography>
        </Box>
      )}
      {copyrightConflict && copyrightSummary && (
        <Box className={styles.section}>
          <Typography>
            <strong>Total numbers of copyrighted items to be dealt with</strong>
          </Typography>
          <Typography>
            <strong>Known copyrighted items that are used or reproduced to the Standard/Safety Guideline: </strong>
            {copyrightSummary.totalConflicts}
          </Typography>
          <Typography>
            {copyrightConflict === QuestionResponse.Yes
              ? 'There is at least one known copyrighted items that might be relevant to the Standard/Safety Guideline'
              : 'There is no disclosed copyrighted item'}
          </Typography>
        </Box>
      )}
      {completionConditionCheck && (
        <Box className={styles.section}>
          <Typography>
            <strong>Completion condition check</strong>
          </Typography>
          <Typography>
            <strong>
              The co-chair checks if any Patented Technologies first become known to the TC Chapter on or after the day
              of the issuance of this Letter Ballot? i.e., (Number of patented technologies first became known to the TC
              Chapter on or after the day of the issuance of this Letter Ballot) &gt; 0 in (Total numbers of Patented
              Technologies to be dealt with):{' '}
            </strong>
            {completionConditionCheck}
          </Typography>
          <Typography>
            <strong>Result: </strong>
            {completionConditionCheck === QuestionResponse.Yes
              ? `Sections 'Assessment of disclosed patented technologies' and 'LOA status check of PMPT' shall be completed
                  and recorded for such patented technologies at next scheduled meeting of the TC Chapter. Until then, the
                  TC Chapter shall NOT go to Action (making motion to pass/fail this Document) (see Regulations ¶ 16.4.1.2)
                  Until then this Letter Ballot Review is on hold.`
              : 'Go To Action'}
          </Typography>
        </Box>
      )}
    </>
  );
};

const technologyConflictView = (technologyConflict: TechnologyConflict) => {
  const { id, type, description, dateOfAssessment, isUsed, isJustified, hasLoa: hasLOA } = technologyConflict;

  const label = type === TechnologyType.Patent ? 'Patented Technology' : 'copyrighted item';
  const nextAction =
    type === TechnologyType.Patent
      ? 'Assessment of disclosed copyrighted items'
      : hasLOA
      ? 'Assessment of disclosed (identified) trademark'
      : 'Copyright Release Status';
  const result =
    isUsed === QuestionResponse.No
      ? `No further action is needed for ${label} #1`
      : isJustified === QuestionResponse.No
      ? 'The Document is failed and returned to the TF'
      : hasLOA === QuestionResponse.Yes
      ? `Proceed to assess next one, or if this is the last one, Go To ${nextAction}.`
      : hasLOA === QuestionResponse.No
      ? 'Motion Vote'
      : `Proceed to assess next one, or if this is the last one, Go To ${nextAction}.`;

  return (
    <Box className={styles.section}>
      <Typography>
        <strong>
          {type === TechnologyType.Patent ? 'Patented Technology' : hasLOA ? 'Copyright Release' : 'Copyright Item'} #
          {id}
        </strong>
      </Typography>
      <Typography>
        <strong>Title and number:</strong> {description}
      </Typography>
      {dateOfAssessment && (
        <Typography>
          <strong>Date:</strong> {getFormattedDate(dateOfAssessment ?? '', 'L')}
        </Typography>
      )}
      {isUsed && (
        <Typography>
          <strong>
            {type === TechnologyType.Patent
              ? 'Is disclosed patented technology #1 found to be "might be material" to the Standard/Safety Guideline?: '
              : 'Is disclosed copyrighted item #1 used or reproduced in the Standard/Safety Guideline?: '}
          </strong>
          {isUsed}
        </Typography>
      )}
      {isJustified && (
        <Typography>
          <strong>
            {type === TechnologyType.Patent
              ? 'Is the use of this PMPT technically justified?: '
              : 'Is the use/reproduction of this copyrighted item technically justified?: '}
          </strong>
          {isJustified}
        </Typography>
      )}
      {hasLOA && (
        <Typography>
          <strong>
            {type === TechnologyType.Patent
              ? 'Has an LOA for this patented technology been received from every owner?: '
              : 'Has the copyright release been received from its owner?: '}
          </strong>
          {hasLOA}
        </Typography>
      )}
      {result && (
        <Typography>
          <strong>Result: </strong>
          {result}
        </Typography>
      )}
    </Box>
  );
};

const ActivityLogViewCard: React.FunctionComponent<ActivityLogViewCardProps> = ({ activityLog }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isStaff } = useAuthContext();
  const [loading, setLoading] = useState<boolean>(false);

  const {
    id,
    meetingId,
    occurredAt,
    connectionStatus,
    motion,
    votingSession,
    document,
    reject,
    technicalIssue,
    comment,
    approvalLevel,
    ipCheckSession,
    technologyConflict,
  } = activityLog;

  const handleDelete = useCallback(() => {
    setLoading(true);
    MeetingService.deleteActivityLog(meetingId, id)
      .then(() => enqueueSnackbar(`Removed activity log`, { variant: 'success' }))
      .catch((error: string) => {
        enqueueSnackbar(`Failed to remove activity log`, defaultSnackbarErrorProps);
        console.error('Error removing activity log:', error);
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar, id, meetingId]);

  return (
    <Card
      title={
        <Typography variant="subtitle1" className={styles.title}>
          {activityTypeLabel[activityLog.type]}
        </Typography>
      }
      bordered
      headerAction={
        isStaff ? (
          <Button color={'error'} variant={'contained'} disabled={loading} onClick={handleDelete}>
            Delete
          </Button>
        ) : (
          <></>
        )
      }
    >
      <Typography>
        <strong>{'Timestamp: '}</strong>
        {getFormattedDate(occurredAt ?? '', 'L LT')}
      </Typography>
      <Typography>
        <strong>{activityLog.type === ActivityType.Connection ? 'User' : 'Staff'}:</strong> {activityLog.staffName}
      </Typography>
      {connectionStatus && (
        <Typography>
          <strong>Status:</strong> {connectionStatus}
        </Typography>
      )}
      {document && documentView(document)}
      {reject && rejectView(reject)}
      {technicalIssue && technicalIssueView(technicalIssue)}
      {comment && commentView(comment)}
      {approvalLevel && approvalLevelView(approvalLevel)}
      {ipCheckSession && ipCheckSessionView(ipCheckSession)}
      {technologyConflict && technologyConflictView(technologyConflict)}
      {motion && motionView(motion, votingSession)}
    </Card>
  );
};

export default ActivityLogViewCard;
